import React, { useState, useEffect, lazy } from 'react';
import { Routes, Route, useNavigate, useLocation, useParams } from 'react-router-dom';
import { useCart } from './context/CartContext';
import { useAuth } from './context/AuthContext'; 
import { useIdleTimer } from './hooks/useIdleTimer'; 
import MarketplaceLayout from './components/layout/MarketplaceLayout';
import handleCheckout from './utils/handleCheckout.js';
import './App.css';

// Project imports
import Loadable from './components/common/Loadable';

// Lazy-loaded components
const HomeDietalase = Loadable(lazy(() => import('./pages/home/HomeDietalase')));
const CartView = Loadable(lazy(() => import('./pages/cart/CartView')));
const ProductDetail = Loadable(lazy(() => import('./pages/product/ProductDetail')));
const CategoryPage = Loadable(lazy(() => import('./pages/category/CategoryPage')));
const Checkout = Loadable(lazy(() => import('./pages/transaction/Checkout')));
const Invoice = Loadable(lazy(() => import('./pages/transaction/Invoice')));
const SsoCallback = Loadable(lazy(() => import('./pages/auth/SsoCallback'))); 
const ProfilePage = Loadable(lazy(() => import('./pages/profile/ProfilePage')));

// =====================================================================
// [FUNGSI BARU] Mengubah "Nama Produk" menjadi format URL (Slug)
// Contoh: "Kertas HVS A4" -> "kertas-hvs-a4"
// =====================================================================
export const generateSlug = (text) => {
  if (!text) return 'produk-dietalase';
  return text.toString().toLowerCase()
    .replace(/\s+/g, '-')           // Ganti spasi dengan tanda strip
    .replace(/[^\w\-]+/g, '')       // Hapus karakter selain huruf/angka/strip
    .replace(/\-\-+/g, '-')         // Hapus strip ganda
    .replace(/^-+/, '')             // Hapus strip di awal
    .replace(/-+$/, '');            // Hapus strip di akhir
};

/**
 * MarketplaceApp - Main component containing routing and state logic.
 */
const MarketplaceApp = () => {
  const {
    cartItems,
    totalPrice,
    addToCart,
    increaseQuantity,
    decreaseQuantity,
    removeItem,
    clearCart,
  } = useCart();
  const { user, isAuthenticated, logout, loading: authLoading } = useAuth(); 

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  // --- ADD IDLE TIMER ---
  const handleIdle = () => {
    if (isAuthenticated) {
      console.log("User has been idle for 3 hours. Logging out.");
      logout();
    }
  };
  useIdleTimer(handleIdle, 3 * 60 * 60 * 1000); 

  // Restore selectedProduct from history state on refresh if possible
  useEffect(() => {
    if (location.pathname.startsWith('/product/') && location.state?.product) {
      setSelectedProduct(location.state.product);
    } else if (window.history.state?.usr?.product) {
        setSelectedProduct(window.history.state.usr.product);
    }
  }, [location]);

  const handleCheckoutClick = (items) => {
    navigate('/checkout', { state: { products: items } });
  };

  // =====================================================================
  // [DIUBAH] Fungsi Klik Produk (Sekarang Menggunakan Nama / Slug)
  // =====================================================================
  const handleProductClick = (product) => {
    // 1. Buat URL dari nama produk
    const slug = generateSlug(product.product_name);
    
    // 2. Navigasi ke URL yang berisi nama produk
    navigate(`/product/${slug}`, { state: { product } });

    // 3. Otomatis gulir ke atas
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  
  const handleCategoryClick = (category) => {
    navigate(category ? `/category/${category.name}` : '/products');
  };

  // =====================================================================
  // [DIUBAH] Pembungkus Detail Produk untuk Mencocokkan Nama di URL
  // =====================================================================
  const ProductDetailWrapper = () => {
    // Mengambil "productSlug" (nama URL) dari parameter browser
    const { productSlug } = useParams();
    const location = useLocation(); 

    useEffect(() => {
        const productFromState = location.state?.product || window.history.state?.usr?.product;

        if (productFromState) {
            // Kita ubah nama produk dari data menjadi format slug URL
            const expectedSlug = generateSlug(productFromState.product_name);
            
            // Cek apakah slug di URL cocok dengan data yang dibawa
            if (expectedSlug === productSlug) {
                if (!selectedProduct || selectedProduct.id !== productFromState.id) {
                    setSelectedProduct(productFromState);
                }
            } else {
                console.warn(`URL tidak valid. Mengalihkan ke Beranda.`);
                navigate('/');
            }
        } 
        else if (!selectedProduct || generateSlug(selectedProduct.product_name) !== productSlug) {
            console.warn(`Data produk kosong. Mengalihkan ke Beranda.`);
            navigate('/');
        }
    }, [productSlug, location.state, navigate, selectedProduct]);

    // Tampilkan loading jika belum sinkron
    if (!selectedProduct || generateSlug(selectedProduct.product_name) !== productSlug) {
        return <div style={{ padding: '50px', textAlign: 'center' }}>Memuat detail produk...</div>;
    }

    return (
      <ProductDetail
        product={selectedProduct}
        onAddToCart={addToCart}
        onBack={() => navigate(-1)}
        onProductClick={handleProductClick}
      />
    );
  };

  const CategoryPageWrapper = () => {
    const { categoryName } = useParams();
    return (
      <CategoryPage
        category={categoryName}
        onProductClick={handleProductClick}
        onBack={() => navigate('/')}
      />
    );
  };

  const AllProductsWrapper = () => (
    <CategoryPage
      onProductClick={handleProductClick}
      onBack={() => navigate('/')}
    />
  );

  return (
    <MarketplaceLayout
      cartItemCount={cartItems.length}
      onSearch={setSearchQuery}
      isAuthenticated={isAuthenticated}
      user={user}
      onLogout={logout}
      authLoading={authLoading}
      onCartClick={() => navigate('/cart')}
      onHomeClick={() => navigate('/')}
    >
      <Routes>
        <Route path="/auth/sso/callback" element={<SsoCallback />} />
        <Route
          path="/"
          element={<HomeDietalase onProductClick={handleProductClick} onCategoryClick={handleCategoryClick} searchQuery={searchQuery} />}
        />
        <Route
          path="/cart"
          element={
            <CartView
              cartItems={cartItems}
              totalPrice={totalPrice}
              onIncreaseQuantity={increaseQuantity}
              onDecreaseQuantity={decreaseQuantity}
              onRemoveItem={removeItem}
              onCheckout={handleCheckoutClick}
              onBack={() => navigate(-1)}
            />
          }
        />
        
        {/* [DIUBAH] Route untuk produk sekarang menggunakan :productSlug bukan :productId */}
        <Route path="/product/:productSlug" element={<ProductDetailWrapper />} />
        
        <Route path="/category/:categoryName" element={<CategoryPageWrapper />} />
        <Route path="/products" element={<AllProductsWrapper />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/invoice/:orderId" element={<Invoice />} />
        <Route path="/profile" element={<ProfilePage />} />
      </Routes>
    </MarketplaceLayout>
  );
};

const App = () => {
  return <MarketplaceApp />;
};

export default App;